/** `x-widget: "object-form"` — edits an object with a KNOWN set of properties
 *  (from the schema) as a form, in a dialog. Used for `xml_builder_options`.
 *
 * The grid cell shows an "N options set" summary + an "Edit…" button. The dialog
 * renders one labelled field per `schema.properties` entry (via `SchemaValueField`).
 * On save, empty/false/default values are dropped so the stored object stays
 * minimal; any keys present in the value but not in `properties` are preserved.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import type { SchemaProp } from '../../config/schema-grid/types';
import { SchemaValueField } from '../../config/schema-grid/ValueCell';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function asObject(v: unknown): Record<string, unknown> {
  return isPlainObject(v) ? v : {};
}

function isEmpty(v: unknown): boolean {
  if (v == null || v === '' || v === false) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (isPlainObject(v)) return Object.keys(v).length === 0;
  return false;
}

interface DialogProps {
  schema: SchemaProp | null;
  value: unknown;
  readOnly: boolean;
  title: string;
  onClose: () => void;
  onDone: (next: Record<string, unknown>) => void;
}

function ObjectFormDialog({ schema, value, readOnly, title, onClose, onDone }: DialogProps) {
  const props = (schema?.properties ?? {}) as Record<string, SchemaProp>;
  const [draft, setDraft] = useState<Record<string, unknown>>(() => asObject(value));

  useEffect(() => {
    setDraft(asObject(value));
  }, [value]);

  function setField(key: string, v: unknown) {
    setDraft((prev) => ({ ...prev, [key]: v }));
  }

  function done() {
    const out: Record<string, unknown> = {};
    // Keep all non-empty keys (incl. any extras not in the schema).
    for (const key of Object.keys(draft)) {
      if (!isEmpty(draft[key])) out[key] = draft[key];
    }
    onDone(out);
  }

  const keys = Object.keys(props);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {keys.length === 0 && (
            <Typography variant="body2" color="text.disabled">
              No known options for this key.
            </Typography>
          )}
          {keys.map((key) => {
            const prop = props[key];
            if (!prop) return null;
            return (
              <SchemaValueField
                key={key}
                prop={prop}
                label={key}
                value={draft[key]}
                onChange={(v) => setField(key, v)}
                readOnly={readOnly}
              />
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={done} disabled={readOnly}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface CellProps {
  schema: SchemaProp | null;
  value: unknown;
  onChange: (next: Record<string, unknown>) => void;
  readOnly?: boolean;
  title?: string;
}

export function ObjectFormCell({
  schema,
  value,
  onChange,
  readOnly = false,
  title = 'Options',
}: CellProps) {
  const [open, setOpen] = useState(false);
  const count = Object.keys(asObject(value)).length;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
        {count} option{count !== 1 ? 's' : ''} set
      </Typography>
      <Button
        size="small"
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', flexShrink: 0 }}
      >
        {readOnly ? 'View…' : 'Edit…'}
      </Button>
      {open && (
        <ObjectFormDialog
          schema={schema}
          value={value}
          readOnly={readOnly}
          title={title}
          onClose={() => setOpen(false)}
          onDone={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
      )}
    </Box>
  );
}
